import axios, { AxiosInstance } from "axios";
import { getCookie, setCookie } from "../utils/cookies";
import { hitRefreshToken } from "./user";
import { baseURL } from "../utils/constants";
import {
  beginInitialRequest,
  completeInitialRequest,
} from "../utils/slowNetwork";

let accessToken = getCookie("accessToken") || "";

export const apiClient: AxiosInstance = axios.create({
  baseURL: baseURL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

// Update the Axios instance directly after successfully refreshing the token.
const updateApiClientToken = (newToken: string) => {
  apiClient.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
  accessToken = newToken;
};

// Mutex to prevent multiple simultaneous refresh requests
let refreshPromise: Promise<string | undefined> | null = null;

type SlowRequestMeta = {
  trackInitial: boolean;
};

const shouldTrackSlowRequest = (config: any) =>
  String(config?.method || "get").toLowerCase() === "get";

const attachSlowRequestTimer = (config: any) => {
  if (!shouldTrackSlowRequest(config)) return;
  const trackInitial = beginInitialRequest();
  if (!trackInitial) return;
  const meta: SlowRequestMeta = {
    trackInitial,
  };
  config.__slowRequestMeta = meta;
};

const clearSlowRequestTimer = (config: any) => {
  const meta = config?.__slowRequestMeta as SlowRequestMeta | undefined;
  if (!meta) return;
  if (meta.trackInitial) {
    completeInitialRequest(false);
  }
  delete config.__slowRequestMeta;
};

const handleRefresh = async (): Promise<string | undefined> => {
  // If a refresh is already in progress, wait for it instead of making another request
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const refresh_data = await hitRefreshToken();

      if (refresh_data?.status === 200) {
        console.log("Token refreshed successfully");
        setCookie("accessToken", refresh_data.data.result.access_token, 1);
        updateApiClientToken(refresh_data.data.result.access_token);
        return refresh_data.data.result.access_token;
      }
      return undefined;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Adding interceptors to handle token refresh
apiClient.interceptors.request.use(
  (config) => {
    // Read fresh token from cookie before every request (cookie = single source of truth)
    const token = getCookie("accessToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    } else {
      delete config.headers["Authorization"];
    }
    attachSlowRequestTimer(config);
    return config;
  },
  (error) => {
    clearSlowRequestTimer(error?.config);
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    const config: any = response?.config;
    const meta = config?.__slowRequestMeta as SlowRequestMeta | undefined;
    if (meta?.trackInitial) {
      completeInitialRequest(response?.data !== undefined);
      delete config.__slowRequestMeta;
    } else {
      clearSlowRequestTimer(response.config);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error?.config;
    clearSlowRequestTimer(originalRequest);
    if (error.response?.status === 403 && !originalRequest?._retry) {
      originalRequest._retry = true;

      const newToken = await handleRefresh();
      if (newToken) {
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
